import { Autocomplete, Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography, useTheme } from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from "@mui/lab";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs from "dayjs";
import { v4 as uuidv4 } from 'uuid';
import { IPAddressDialog } from "./IPAddressDialog";
import { getClients } from "../../../../redux/clientsSlice";
import { useSelector } from "react-redux";

const API_PATH_PATTERN = /^\/[A-Za-z0-9._{}-]+(?:\/[A-Za-z0-9._{}-]+)*\/?$/;

const createDefaultProfileDataByKey = (dataKey) => {
    if (dataKey === 'application') {
        return {
            id: uuidv4(),
            profile_name: '',
            ip_address: null,
            date_time: null,
        };
    }

    return {
        id: uuidv4(),
        profile_name: '',
        action: [],
        environment_resource_name: '',
        ip_address: null,
        date_time: null,
    };
};

export const ProfileBuilderManagement = ({ open, title, setOpen, event, onSubmit }) => {
    const theme = useTheme();

    const clients = useSelector(getClients);

    const [profileData, setProfileData] = useState(null);
    const [waiting, setWaiting] = useState(false);
    const [openIpAddressDialog, setOpenIpAddressDialog] = useState(false);
    const [applicationList, setApplicationList] = useState([]);
    const [apiList, setApiList] = useState([]);
    const [profileNameTouched, setProfileNameTouched] = useState(false);
    const [pathTouched, setPathTouched] = useState(false);
    const [dateTimeValidationError, setDateTimeValidationError] = useState(false);

    const normalizedProfileName = String(profileData?.profile_name || '').trim();
    const hasValidProfileName = normalizedProfileName.length > 0;
    const profileNameError = !hasValidProfileName;
    const showProfileNameError = profileNameTouched && profileNameError;
    const profileNameHelperText = 'Please enter profile name.';

    const hasActionField = profileData && Object.prototype.hasOwnProperty.call(profileData, 'action');
    const hasValidActionSelection = !hasActionField || (Array.isArray(profileData?.action) && profileData.action.length > 0);

    const normalizedPath = String(profileData?.path || '').trim();
    const isRestfulApi = event.dataKey === 'restful_api';
    const isPathEmpty = normalizedPath.length === 0;
    const hasWildcardInPath = normalizedPath.includes('*');
    const isApiPathPatternValid = API_PATH_PATTERN.test(normalizedPath) && !hasWildcardInPath;
    const hasValidPathForRestfulApi = !isRestfulApi || (!isPathEmpty && isApiPathPatternValid);
    const pathError = isRestfulApi && !hasValidPathForRestfulApi;
    const showPathError = pathTouched && pathError;
    const pathHelperText = isPathEmpty
        ? 'Please enter endpoint path.'
        : hasWildcardInPath
            ? 'Wildcard "*" is not supported.'
            : 'Invalid path format. Example: /users, /users/{id}, /v1/profile';
    const normalizedDateTime = profileData?.date_time;
    const hasValidDateTime = !dateTimeValidationError && (!normalizedDateTime || dayjs(normalizedDateTime).isValid());
    const disableSubmit = waiting
        || !hasValidProfileName
        || !hasValidActionSelection
        || !hasValidPathForRestfulApi
        || !hasValidDateTime;

    const handleClose = () => {
        setOpen(false);
        setProfileData(null);
        setWaiting(false);
        setProfileNameTouched(false);
        setPathTouched(false);
        setDateTimeValidationError(false);
    };

    const handleOpenIpAddressDialog = () => {
        setOpenIpAddressDialog(true);
    };

    const handleResourceNameChange = (nextValue) => {
        setProfileData((prev) => ({
            ...(prev || {}),
            environment_resource_name: String(nextValue ?? ''),
        }));
    };

    useEffect(() => {
        if (event.action === 'create' && event.dataKey) {
            const newProfileData = createDefaultProfileDataByKey(event.dataKey);
            setProfileData(newProfileData);
            setProfileNameTouched(false);
            setPathTouched(false);
            setDateTimeValidationError(false);
        } else if (event.action === 'update' && event.dataKey) {
            // console.log('ProfileBuilderManagement event update:', event);
            const defaults = createDefaultProfileDataByKey(event.dataKey);
            const sourceItemRaw = event?.item && typeof event.item === 'object' ? event.item : {};
            const { action: ignoredAction, ...sourceItem } = sourceItemRaw;
            const updateProfileData = {
                ...defaults,
                ...sourceItem,
                id: sourceItem?.id || sourceItem?.profile_id || defaults.id,
                // profile_id: sourceItem?.profile_id || sourceItem?.id || defaults.id,
                profile_name: sourceItem?.profile_name || '',
                path: sourceItem?.path || '',
                environment_resource_name: sourceItem?.environment_resource_name || '',
                ip_address: sourceItem?.ip_address ?? null,
                date_time: sourceItem?.date_time ?? null,
                ...(event.dataKey !== 'application' ? {
                    action: Array.isArray(sourceItemRaw?.action)
                        ? sourceItemRaw.action
                        : (Array.isArray(defaults.action) ? defaults.action : undefined),
                } : {}),
            };
            setProfileData(updateProfileData);
            setProfileNameTouched(false);
            setPathTouched(false);
            setDateTimeValidationError(false);
        } else {
            return;
        }
    }, [event]);

    const handleSubmit = async (data) => {
        if (!data || typeof onSubmit !== 'function') {
            return;
        }

        const normalizedData = {
            ...data,
            profile_name: String(data?.profile_name || '').trim(),
            environment_resource_name: String(data?.environment_resource_name || '').trim(),
            path: String(data?.path || '').trim(),
        };

        const isProfileNameValidForSubmit = normalizedData.profile_name.length > 0;
        const isPathValidForSubmit = event.dataKey === 'restful_api'
            ? normalizedData.path.length > 0 && !normalizedData.path.includes('*') && API_PATH_PATTERN.test(normalizedData.path)
            : true;
        const isDateTimeValidForSubmit = !normalizedData?.date_time || dayjs(normalizedData.date_time).isValid();

        if (!isProfileNameValidForSubmit || !isPathValidForSubmit || !isDateTimeValidForSubmit || dateTimeValidationError) {
            setProfileNameTouched(true);
            setPathTouched(true);
            return;
        }

        setWaiting(true);
        try {
            const isSaved = await onSubmit(normalizedData, event);
            if (isSaved) {
                handleClose();
            }
        } catch (error) {
            console.error('Failed to save profile builder data:', error);
        } finally {
            setWaiting(false);
        }
    };

    const normalizeLabelForEnvironmentProperties = (dataKey) => {
        if (dataKey === 'application') {
            return '(User)';
        } else if (dataKey === 'restful_api') {
            return '(Application)';
        } else if (dataKey === 'data') {
            return '(RESTful-API)';
        } else {
            return '';
        }
    };

    useEffect(() => {
        if (open && event.dataKey === 'restful_api') {
            const appList = clients.filter(client => client.app_type === 'WEB' || client.app_type === 'NATIVE');
            setApplicationList(appList);
        } else if (open && event.dataKey === 'data') {
            const apiList = clients.filter(client => client.app_type === 'API');
            setApiList(apiList);
        }
    }, [clients, open, event.dataKey]);

    return (
        <Fragment>
            <Dialog
                fullWidth={true}
                maxWidth={"sm"}
                open={open}
            >
                <DialogTitle
                    component="div"
                    sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.white.main, paddingY: 1, paddingX: 2 }}
                    className='flex justify-between items-center'
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{event.title}</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            padding: 0,
                            color: theme.palette.white.main
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: '25px !important', px: '25px', pb: '25px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="subtitle2" sx={{ minWidth: 120, m: 0, flexShrink: 0 }}>
                                Profile Name
                            </Typography>
                            <TextField
                                required
                                size='small'
                                id="profile_name"
                                fullWidth
                                value={profileData?.profile_name || ''}
                                onChange={(e) => setProfileData({ ...profileData, profile_name: e.target.value })}
                                onBlur={() => setProfileNameTouched(true)}
                                placeholder="Please enter profile name."
                                error={showProfileNameError}
                                helperText={showProfileNameError ? profileNameHelperText : ''}
                                sx={{
                                    '& .MuiInputBase-input::placeholder': {
                                        fontSize: 14,
                                        // opacity: 1,
                                    },
                                    "& .MuiInputBase-input": {
                                        fontSize: 14,
                                    },
                                }}
                            />
                        </Box>

                        {Array.isArray(profileData?.action) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="subtitle2" sx={{ minWidth: 120, m: 0, flexShrink: 0 }}>
                                    Action
                                </Typography>
                                <Autocomplete
                                    id="action"
                                    fullWidth
                                    multiple
                                    disableCloseOnSelect
                                    size='small'
                                    options={event.dataKey === 'restful_api' ? ['GET', 'POST', 'PUT', 'DELETE'] : ['SELECT', 'INSERT', 'UPDATE', 'DELETE']}
                                    disableClearable
                                    value={profileData?.action || []}
                                    onChange={(event, newValue) => {
                                        setProfileData({ ...profileData, action: newValue });
                                    }}
                                    getOptionLabel={(option) => option}
                                    renderOption={(props, option, { selected }) => {
                                        const { key, ...optionProps } = props;
                                        return (
                                            <li key={key} {...optionProps}>
                                                <Checkbox
                                                    checked={selected}
                                                />
                                                {option}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params) => <TextField
                                        {...params}
                                        placeholder={(profileData?.action?.length || 0) > 0 ? "" : "Please select action."}
                                        required
                                        sx={{
                                            '& .MuiInputBase-input::placeholder': {
                                                fontSize: 14,
                                                // opacity: 1,
                                            },
                                            "& .MuiInputBase-input": {
                                                fontSize: 14,
                                            },
                                        }}
                                    />}
                                />
                            </Box>
                        )}

                        {event.dataKey === 'restful_api' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="subtitle2" sx={{ minWidth: 120, m: 0, flexShrink: 0 }}>
                                    Path
                                </Typography>
                                <TextField
                                    required
                                    size='small'
                                    id="path"
                                    fullWidth
                                    value={profileData?.path || ''}
                                    onFocus={() => setPathTouched(true)}
                                    onChange={(e) => setProfileData({ ...profileData, path: e.target.value })}
                                    placeholder="Please enter endpoint path."
                                    error={showPathError}
                                    helperText={showPathError ? pathHelperText : ''}
                                    sx={{
                                        '& .MuiInputBase-input::placeholder': {
                                            fontSize: 14,
                                            // opacity: 1,
                                        },
                                        "& .MuiInputBase-input": {
                                            fontSize: 14,
                                        },
                                    }}
                                />
                            </Box>
                        )}

                        <Typography variant="subtitle1" sx={{ m: 0, mt: 3, fontWeight: 'bold' }}>
                            Environment Properties {normalizeLabelForEnvironmentProperties(event.dataKey)}
                        </Typography>

                        {event.dataKey !== 'application' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="subtitle2" sx={{ minWidth: 120, m: 0, flexShrink: 0 }}>
                                    Resource
                                </Typography>
                                {event.dataKey === 'restful_api' && (
                                    <Autocomplete
                                        freeSolo
                                        id="free-solo-application"
                                        size='small'
                                        fullWidth
                                        disableClearable
                                        value={profileData?.environment_resource_name ?? ''}
                                        inputValue={profileData?.environment_resource_name ?? ''}
                                        options={applicationList.map((option) => option.client_name)}
                                        onChange={(event, newValue) => {
                                            handleResourceNameChange(newValue);
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            handleResourceNameChange(newInputValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label=""
                                                placeholder="Application name."
                                                slotProps={{
                                                    input: {
                                                        ...params.InputProps,
                                                        type: 'search',
                                                    },
                                                }}
                                                sx={{
                                                    '& .MuiInputBase-input::placeholder': {
                                                        fontSize: 14,
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        fontSize: 14,
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                )}
                                {event.dataKey === 'data' && (
                                    <Autocomplete
                                        freeSolo
                                        id="free-solo-api"
                                        size='small'
                                        fullWidth
                                        disableClearable
                                        value={profileData?.environment_resource_name ?? ''}
                                        inputValue={profileData?.environment_resource_name ?? ''}
                                        options={apiList.map((option) => option.client_name)}
                                        onChange={(event, newValue) => {
                                            handleResourceNameChange(newValue);
                                        }}
                                        onInputChange={(event, newInputValue) => {
                                            handleResourceNameChange(newInputValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label=""
                                                placeholder="RESTful-API name."
                                                slotProps={{
                                                    input: {
                                                        ...params.InputProps,
                                                        type: 'search',
                                                    },
                                                }}
                                                sx={{
                                                    '& .MuiInputBase-input::placeholder': {
                                                        fontSize: 14,
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        fontSize: 14,
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="subtitle2" sx={{ minWidth: 120, m: 0, flexShrink: 0 }}>
                                IP Address
                            </Typography>
                            <TextField
                                size='small'
                                id="ip_address"
                                fullWidth
                                value={profileData?.ip_address ?? ''}
                                placeholder="Click to set IP address."
                                onClick={() => handleOpenIpAddressDialog()}
                                InputProps={{
                                    readOnly: true,
                                }}
                                sx={{
                                    '& .MuiInputBase-input': {
                                        caretColor: 'transparent',
                                        cursor: 'pointer',
                                        fontSize: 14,
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        fontSize: 14,
                                        // opacity: 1,
                                    },
                                }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="subtitle2" sx={{ minWidth: 120, m: 0, flexShrink: 0 }}>
                                Date Time
                            </Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    value={profileData?.date_time && dayjs(profileData.date_time).isValid() ? dayjs(profileData.date_time) : null}
                                    ampm={false}
                                    format="YYYY-MM-DD HH:mm:ss"
                                    timeSteps={{ minutes: 1 }}
                                    onChange={(newValue, context) => {
                                        const hasError = Boolean(context?.validationError);
                                        setDateTimeValidationError(hasError);

                                        if (hasError) {
                                            return;
                                        }

                                        setProfileData((prev) => ({
                                            ...(prev || {}),
                                            date_time: newValue && dayjs(newValue).isValid()
                                                ? dayjs(newValue).format("YYYY-MM-DD HH:mm:ss")
                                                : null,
                                        }));
                                    }}
                                    onError={(reason) => {
                                        setDateTimeValidationError(Boolean(reason));
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: "small",
                                            variant: "outlined",
                                            error: dateTimeValidationError,
                                            helperText: dateTimeValidationError ? 'Invalid date time. Please correct the value or clear it.' : '',
                                            sx: {
                                                "& .MuiInputBase-input": {
                                                    fontSize: 14,
                                                },
                                            },
                                        },
                                    }}
                                    sx={{ flex: 1 }}
                                />
                            </LocalizationProvider>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <div className='px-4 pb-[15px]'>
                        <LoadingButton variant="contained" loading={waiting} disabled={disableSubmit} onClick={() => handleSubmit(profileData)}>
                            {event.action === 'create' ? 'Create' : 'Update'}
                        </LoadingButton>
                    </div>
                </DialogActions>
            </Dialog>
            <IPAddressDialog
                open={openIpAddressDialog}
                onClose={() => setOpenIpAddressDialog(false)}
                onSave={(ipAddress) => {
                    setProfileData({ ...profileData, ip_address: ipAddress });
                    setOpenIpAddressDialog(false);
                }}
                ipAddress={profileData?.ip_address || ''}
            />
        </Fragment>
    )
};